import React, { useState, useEffect } from 'react';

const QUESTION_TYPES = [
  { value: 'multiple_choice', label: 'Multiple Choice (single select)' },
  { value: 'checkbox',        label: 'Checkbox (multi-select)' },
  { value: 'dropdown',        label: 'Dropdown (single select)' },
  { value: 'rating',          label: 'Rating Scale' },
  { value: 'text',            label: 'Short Text' },
  { value: 'textarea',        label: 'Long Text / Paragraph' },
  { value: 'yes_no',          label: 'Yes / No' },
  { value: 'date',            label: 'Date Picker' },
];

const CHOICE_TYPES = new Set(['multiple_choice', 'checkbox', 'dropdown']);

const EMPTY_OPTION = () => ({ option_text: '', display_order: 0 });

const EMPTY_QUESTION = () => ({
  question_text: '',
  question_type: 'multiple_choice',
  is_required: true,
  display_order: 0,
  helper_text: '',
  rating_max: 5,
  rating_label_low: '',
  rating_label_high: '',
  options: [EMPTY_OPTION(), EMPTY_OPTION()],
});

const EMPTY_FORM = {
  title: '',
  description: '',
  status: 'draft',
  is_anonymous: false,
  allow_multiple_responses: false,
  start_date: '',
  end_date: '',
  response_limit: '',
  thank_you_message: '',
  questions: [EMPTY_QUESTION()],
};

const SurveyForm = ({ survey, onSave, onClose, submitting }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  // Populate form when editing an existing survey
  useEffect(() => {
    if (survey) {
      setFormData({
        title:                    survey.title || '',
        description:              survey.description || '',
        status:                   survey.status || 'draft',
        is_anonymous:             !!survey.is_anonymous,
        allow_multiple_responses: !!survey.allow_multiple_responses,
        start_date:               survey.start_date ? survey.start_date.slice(0, 16) : '',
        end_date:                 survey.end_date   ? survey.end_date.slice(0, 16)   : '',
        response_limit:           survey.response_limit ?? '',
        thank_you_message:        survey.thank_you_message || '',
        questions: (survey.questions && survey.questions.length > 0)
          ? survey.questions.map(q => ({
              question_text:     q.question_text || '',
              question_type:     q.question_type || 'multiple_choice',
              is_required:       q.is_required !== false,
              display_order:     q.display_order ?? 0,
              helper_text:       q.helper_text || '',
              rating_max:        q.rating_max || 5,
              rating_label_low:  q.rating_label_low || '',
              rating_label_high: q.rating_label_high || '',
              options: (q.options && q.options.length > 0)
                ? q.options.map(o => ({ option_text: o.option_text || '', display_order: o.display_order ?? 0 }))
                : [EMPTY_OPTION(), EMPTY_OPTION()],
            }))
          : [EMPTY_QUESTION()],
      });
    } else {
      setFormData(EMPTY_FORM);
    }
    setErrors({});
  }, [survey]);

  // ─── Header field handlers ──────────────────────────────────────────────

  const handleField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  // ─── Question handlers ──────────────────────────────────────────────────

  const updateQuestion = (qi, field, value) => {
    setFormData(prev => {
      const questions = prev.questions.map((q, i) => {
        if (i !== qi) return q;
        const updated = { ...q, [field]: value };
        // When type changes, reset options if type requires choices
        if (field === 'question_type' && CHOICE_TYPES.has(value) && (!q.options || q.options.length === 0)) {
          updated.options = [EMPTY_OPTION(), EMPTY_OPTION()];
        }
        return updated;
      });
      return { ...prev, questions };
    });
    if (errors[`q${qi}_text`]) setErrors(prev => ({ ...prev, [`q${qi}_text`]: null }));
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...EMPTY_QUESTION(), display_order: prev.questions.length }],
    }));
  };

  const removeQuestion = (qi) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== qi).map((q, i) => ({ ...q, display_order: i })),
    }));
  };

  const moveQuestion = (qi, dir) => {
    setFormData(prev => {
      const qs = [...prev.questions];
      const target = qi + dir;
      if (target < 0 || target >= qs.length) return prev;
      [qs[qi], qs[target]] = [qs[target], qs[qi]];
      return { ...prev, questions: qs.map((q, i) => ({ ...q, display_order: i })) };
    });
  };

  // ─── Option handlers ────────────────────────────────────────────────────

  const updateOption = (qi, oi, value) => {
    setFormData(prev => {
      const questions = prev.questions.map((q, i) => {
        if (i !== qi) return q;
        const options = q.options.map((o, j) => j === oi ? { ...o, option_text: value } : o);
        return { ...q, options };
      });
      return { ...prev, questions };
    });
    if (errors[`q${qi}_opt${oi}`]) setErrors(prev => ({ ...prev, [`q${qi}_opt${oi}`]: null }));
  };

  const addOption = (qi) => {
    setFormData(prev => {
      const questions = prev.questions.map((q, i) => {
        if (i !== qi) return q;
        return { ...q, options: [...q.options, { option_text: '', display_order: q.options.length }] };
      });
      return { ...prev, questions };
    });
  };

  const removeOption = (qi, oi) => {
    setFormData(prev => {
      const questions = prev.questions.map((q, i) => {
        if (i !== qi) return q;
        const options = q.options.filter((_, j) => j !== oi).map((o, j) => ({ ...o, display_order: j }));
        return { ...q, options };
      });
      return { ...prev, questions };
    });
  };

  // ─── Validation ─────────────────────────────────────────────────────────

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Survey title is required';
    if (formData.start_date && formData.end_date && new Date(formData.end_date) <= new Date(formData.start_date)) {
      newErrors.end_date = 'End date must be after start date';
    }
    if (formData.response_limit && (isNaN(formData.response_limit) || Number(formData.response_limit) < 1)) {
      newErrors.response_limit = 'Must be a positive number';
    }
    formData.questions.forEach((q, qi) => {
      if (!q.question_text.trim()) newErrors[`q${qi}_text`] = 'Question text is required';
      if (CHOICE_TYPES.has(q.question_type)) {
        const filled = q.options.filter(o => o.option_text.trim());
        if (filled.length < 2) newErrors[`q${qi}_opts`] = 'At least 2 options required';
        q.options.forEach((o, oi) => {
          if (!o.option_text.trim() && q.options.length > 1) {
            newErrors[`q${qi}_opt${oi}`] = 'Option cannot be empty';
          }
        });
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const payload = {
      ...formData,
      start_date:     formData.start_date     ? new Date(formData.start_date).toISOString()  : null,
      end_date:       formData.end_date       ? new Date(formData.end_date).toISOString()    : null,
      response_limit: formData.response_limit ? parseInt(formData.response_limit, 10)        : null,
      questions: formData.questions.map((q, qi) => ({
        ...q,
        display_order: qi,
        options: CHOICE_TYPES.has(q.question_type)
          ? q.options.filter(o => o.option_text.trim()).map((o, oi) => ({ ...o, display_order: oi }))
          : [],
      })),
    };

    onSave(payload);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="survey-form-container">
      <div className="survey-form-header">
        <h3>{survey ? '✏️ Edit Survey' : '➕ Create New Survey'}</h3>
        <button className="close-btn" onClick={onClose} type="button">×</button>
      </div>

      <form onSubmit={handleSubmit} className="survey-form" noValidate>

        {/* ── Header Section ────────────────────────────────────────── */}
        <div className="survey-form-section">
          <h4 className="survey-section-title">📝 Survey Details</h4>

          <div className="form-group">
            <label>Title <span className="required">*</span></label>
            <input
              type="text"
              value={formData.title}
              onChange={e => handleField('title', e.target.value)}
              className={errors.title ? 'input-error' : ''}
              placeholder="e.g. Employee Satisfaction Survey Q2 2026"
            />
            {errors.title && <span className="field-error">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={e => handleField('description', e.target.value)}
              placeholder="Optional intro text shown to respondents"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Status</label>
              <select value={formData.status} onChange={e => handleField('status', e.target.value)}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Response Limit</label>
              <input
                type="number"
                value={formData.response_limit}
                onChange={e => handleField('response_limit', e.target.value)}
                className={errors.response_limit ? 'input-error' : ''}
                placeholder="Unlimited"
                min={1}
              />
              {errors.response_limit && <span className="field-error">{errors.response_limit}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Start Date</label>
              <input
                type="datetime-local"
                value={formData.start_date}
                onChange={e => handleField('start_date', e.target.value)}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>End Date</label>
              <input
                type="datetime-local"
                value={formData.end_date}
                onChange={e => handleField('end_date', e.target.value)}
                className={errors.end_date ? 'input-error' : ''}
              />
              {errors.end_date && <span className="field-error">{errors.end_date}</span>}
            </div>
          </div>

          <div className="form-row">
            <label className="survey-toggle-label">
              <input
                type="checkbox"
                checked={formData.is_anonymous}
                onChange={e => handleField('is_anonymous', e.target.checked)}
              />
              <span>Anonymous responses (don't collect name/email)</span>
            </label>
            <label className="survey-toggle-label">
              <input
                type="checkbox"
                checked={formData.allow_multiple_responses}
                onChange={e => handleField('allow_multiple_responses', e.target.checked)}
              />
              <span>Allow multiple responses per email</span>
            </label>
          </div>

          <div className="form-group">
            <label>Thank You Message</label>
            <input
              type="text"
              value={formData.thank_you_message}
              onChange={e => handleField('thank_you_message', e.target.value)}
              placeholder="Thank you for completing the survey!"
            />
          </div>
        </div>

        {/* ── Questions ─────────────────────────────────────────────── */}
        <div className="survey-form-section">
          <div className="survey-section-header">
            <h4 className="survey-section-title">❓ Questions ({formData.questions.length})</h4>
          </div>

          {formData.questions.map((q, qi) => (
            <div key={qi} className="survey-question-card">
              <div className="question-card-header">
                <span className="question-num">Q{qi + 1}</span>
                <div className="question-header-actions">
                  <button type="button" onClick={() => moveQuestion(qi, -1)} disabled={qi === 0} className="q-icon-btn" title="Move up">↑</button>
                  <button type="button" onClick={() => moveQuestion(qi, 1)} disabled={qi === formData.questions.length - 1} className="q-icon-btn" title="Move down">↓</button>
                  <button type="button" onClick={() => removeQuestion(qi)} className="q-icon-btn q-icon-danger" title="Remove question" disabled={formData.questions.length === 1}>🗑️</button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Question Text <span className="required">*</span></label>
                  <input
                    type="text"
                    value={q.question_text}
                    onChange={e => updateQuestion(qi, 'question_text', e.target.value)}
                    className={errors[`q${qi}_text`] ? 'input-error' : ''}
                    placeholder="Enter your question here…"
                  />
                  {errors[`q${qi}_text`] && <span className="field-error">{errors[`q${qi}_text`]}</span>}
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Type</label>
                  <select
                    value={q.question_type}
                    onChange={e => updateQuestion(qi, 'question_type', e.target.value)}
                  >
                    {QUESTION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row" style={{ alignItems: 'center', gap: '24px' }}>
                <label className="survey-toggle-label" style={{ marginBottom: 0 }}>
                  <input
                    type="checkbox"
                    checked={q.is_required}
                    onChange={e => updateQuestion(qi, 'is_required', e.target.checked)}
                  />
                  <span>Required</span>
                </label>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Helper Text</label>
                  <input
                    type="text"
                    value={q.helper_text}
                    onChange={e => updateQuestion(qi, 'helper_text', e.target.value)}
                    placeholder="Optional hint for respondents"
                  />
                </div>
              </div>

              {/* Rating config */}
              {q.question_type === 'rating' && (
                <div className="form-row survey-rating-config">
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Max Rating</label>
                    <select
                      value={q.rating_max}
                      onChange={e => updateQuestion(qi, 'rating_max', parseInt(e.target.value, 10))}
                    >
                      {[5, 7, 10].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Low Label</label>
                    <input
                      type="text"
                      value={q.rating_label_low}
                      onChange={e => updateQuestion(qi, 'rating_label_low', e.target.value)}
                      placeholder="e.g. Not likely"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>High Label</label>
                    <input
                      type="text"
                      value={q.rating_label_high}
                      onChange={e => updateQuestion(qi, 'rating_label_high', e.target.value)}
                      placeholder="e.g. Very likely"
                    />
                  </div>
                </div>
              )}

              {/* Options (choice types) */}
              {CHOICE_TYPES.has(q.question_type) && (
                <div className="survey-options-section">
                  <label className="options-label">Answer Options</label>
                  {errors[`q${qi}_opts`] && <span className="field-error">{errors[`q${qi}_opts`]}</span>}
                  {q.options.map((opt, oi) => (
                    <div key={oi} className="survey-option-row">
                      <span className="option-bullet">{String.fromCharCode(65 + oi)}.</span>
                      <input
                        type="text"
                        value={opt.option_text}
                        onChange={e => updateOption(qi, oi, e.target.value)}
                        className={errors[`q${qi}_opt${oi}`] ? 'input-error' : ''}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                      />
                      <button
                        type="button"
                        className="q-icon-btn q-icon-danger"
                        onClick={() => removeOption(qi, oi)}
                        disabled={q.options.length <= 2}
                        title="Remove option"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button type="button" className="survey-add-option-btn" onClick={() => addOption(qi)}>
                    + Add Option
                  </button>
                </div>
              )}
            </div>
          ))}

          <button type="button" className="survey-add-question-btn" onClick={addQuestion}>
            + Add Question
          </button>
        </div>

        {/* ── Footer Buttons ────────────────────────────────────────── */}
        <div className="survey-form-footer">
          <button type="button" className="action-btn action-btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="action-btn action-btn-primary" disabled={submitting}>
            {submitting ? '⏳ Saving…' : survey ? '💾 Save Changes' : '➕ Create Survey'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SurveyForm;
